
import React, { useState, useEffect } from 'react';
import { Button, Card } from './ui';

const InstallPrompt: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isIos, setIsIos] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed/in standalone mode
    if (window.matchMedia('(display-mode: standalone)').matches || (window.navigator as any).standalone) {
      setIsStandalone(true);
      return;
    }

    // Detect iOS
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIosDevice = /iphone|ipad|ipod/.test(userAgent);
    setIsIos(isIosDevice);

    // Listen for the Chrome/Edge/Android install event
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Only show if we haven't dismissed it in this session
      if (!sessionStorage.getItem('install_prompt_dismissed')) {
        setIsVisible(true);
      }
    };

    // For iOS, show after a short delay since there's no native event
    if (isIosDevice && !sessionStorage.getItem('install_prompt_dismissed')) {
      const timer = setTimeout(() => setIsVisible(true), 5000);
      return () => clearTimeout(timer);
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setIsVisible(false);
    }
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setIsVisible(false);
    sessionStorage.setItem('install_prompt_dismissed', 'true');
  };

  if (isStandalone || !isVisible) return null;

  return (
    <div className="fixed bottom-20 md:bottom-8 left-4 right-4 md:left-auto md:right-8 z-[200] animate-slide-up max-w-sm w-full mx-auto md:mx-0">
      <Card className="p-5 shadow-2xl border-indigo-100 bg-white/95 backdrop-blur-md ring-1 ring-black/5 overflow-hidden">
        <div className="flex gap-4">
          <div className="w-12 h-12 bg-indigo-600 rounded-xl flex items-center justify-center text-2xl shadow-lg shadow-indigo-500/20 flex-shrink-0">
            🎓
          </div>
          <div className="flex-1">
            <h4 className="font-bold text-slate-900 text-sm">Install DAD HUB</h4>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Add the app to your home screen for faster access and a better experience.
            </p>
          </div>
          <button onClick={handleDismiss} className="text-slate-400 hover:text-slate-600">✕</button>
        </div>

        <div className="mt-4 flex flex-col gap-2">
          {isIos ? (
            <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
              <p className="text-[10px] font-bold text-slate-700 flex items-center gap-2">
                <span>1. Tap share button</span>
                <span className="text-lg">⎋</span>
              </p>
              <p className="text-[10px] font-bold text-slate-700 flex items-center gap-2 mt-1">
                <span>2. Select "Add to Home Screen"</span>
                <span className="text-lg">⊞</span>
              </p>
            </div>
          ) : (
            <Button onClick={handleInstallClick} className="w-full !py-2 text-[11px] uppercase tracking-widest font-black">
              Install Locally
            </Button>
          )}
          {!isIos && (
            <button onClick={handleDismiss} className="text-[10px] text-slate-400 font-bold uppercase tracking-widest hover:text-indigo-600 transition-colors py-1">
              Maybe Later
            </button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default InstallPrompt;
